import { useEffect, useState, useCallback } from "react";
import { apiTech } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill } from "@/components/admin";
import JobInvoicePanel from "@/components/JobInvoicePanel";
import PaymentSummary from "@/components/PaymentSummary";
import BrandedInvoice from "@/components/BrandedInvoice";
import {
  X, Phone, User as UserIcon, MapPin, Clock, ChevronRight,
  Truck, MapPinned, FileText, CheckCircle2, Flag, Banknote,
  ClipboardList, PackageOpen,
} from "lucide-react";

const PAYMENT_METHODS = [
  { key: "cash", label: "Cash" },
  { key: "check", label: "Check" },
  { key: "zelle", label: "Zelle" },
  { key: "card", label: "Card" },
];

export default function TechJobDetail({ jobId, onClose }) {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [latestInvoice, setLatestInvoice] = useState(null);

  const reload = useCallback(() => {
    apiTech.get(`/tech/jobs/${jobId}`).then((r) => setJob(r.data)).catch(() => {});
    apiTech.get(`/crm/jobs/${jobId}/invoices`).then((r) => {
      // Most recent non-void invoice
      const list = (r.data || []).filter((i) => i.status !== "void");
      setLatestInvoice(list[0] || null);
    }).catch(() => setLatestInvoice(null));
  }, [jobId]);
  useEffect(() => { reload(); }, [reload]);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  async function action(fn) {
    setBusy(true);
    try { await fn(); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  if (!job) {
    return (
      <Sheet onClose={onClose}>
        <div className="p-6 text-center text-[#FFFFFF]/70">Loading…</div>
      </Sheet>
    );
  }

  const status = job.status;
  const isClosed = status === "completed" || status === "cancelled";

  return (
    <Sheet onClose={onClose}>
      {/* Header */}
      <header className="sticky top-0 bg-[#002060] border-b border-[#FFFFFF]/15 px-4 py-3 flex items-start gap-3 z-10">
        <button
          onClick={onClose}
          data-testid="tech-detail-close"
          aria-label="Close"
          className="shrink-0 w-9 h-9 rounded-md hover:bg-[#FFFFFF]/10 text-[#FFFFFF]/70 grid place-items-center transition"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-[#FFFFFF] capitalize truncate">{job.appliance}</h2>
            {job.manager_flag && <Flag className="w-3.5 h-3.5 text-[#FF6600] shrink-0" />}
          </div>
          <div className="mt-1 flex items-center gap-2">
            <StatusPill status={status} />
            {job.invoice_status === "manual_paid" && (
              <span className="text-[10px] uppercase tracking-wider text-[#FFFFFF] font-semibold">paid</span>
            )}
          </div>
        </div>
      </header>

      <div className="p-4 space-y-4">
        {/* Customer block */}
        <Section title="Customer">
          <Row icon={UserIcon} value={job.customer_name} />
          {job.customer_phone && (
            <Action
              icon={Phone}
              label="Call customer"
              href={`tel:${job.customer_phone}`}
              testid="tech-detail-call"
            >{job.customer_phone}</Action>
          )}
          {job.address && (
            <Action
              icon={MapPin}
              label="Open in maps"
              href={`https://maps.google.com/?q=${encodeURIComponent(job.address)}`}
              external
              testid="tech-detail-maps"
            >{job.address}</Action>
          )}
          {job.scheduled_for && (
            <Row icon={Clock} value={job.scheduled_for} />
          )}
        </Section>

        {/* Issue + diagnosis */}
        <Section title="Issue">
          <p className="text-sm text-[#FFFFFF] leading-relaxed">{job.issue || "—"}</p>
          {job.diagnosis_note && (
            <div className="mt-3 bg-[#002060] border border-[#FFFFFF]/15 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#FFFFFF]/70 font-medium mb-1">Diagnosis</div>
              <p className="text-sm text-[#FFFFFF] whitespace-pre-wrap">{job.diagnosis_note}</p>
            </div>
          )}
          {job.private_tech_note && (
            <div className="mt-3 bg-[#FF6600]/10 border border-[#FF6600] text-[#FF6600] text-xs rounded-md p-2.5">
              <span className="font-semibold uppercase tracking-wider text-[10px]">Private note · </span>
              {job.private_tech_note}
            </div>
          )}
        </Section>

        {/* Money — quoted/final price legacy */}
        {(job.quoted_price != null || job.final_price != null) && (
          <Section title="Money">
            {job.quoted_price != null && (
              <KV k="Quoted" v={`$${Number(job.quoted_price).toFixed(0)}`} />
            )}
            {job.final_price != null && (
              <KV k="Final" v={`$${Number(job.final_price).toFixed(2)}`} tone="emerald" />
            )}
            {job.payment_method && (
              <KV k="Paid via" v={job.payment_method} />
            )}
          </Section>
        )}

        {/* Payment summary — invoice deposit/balance */}
        {latestInvoice && (
          <PaymentSummary
            summary={{
              invoice_number: latestInvoice.invoice_number,
              total: latestInvoice.total,
              amount_paid: latestInvoice.amount_paid,
              balance_due: latestInvoice.balance_due,
              payment_type: latestInvoice.payment_type,
              deposit_required: latestInvoice.deposit_amount_required,
              status: latestInvoice.status,
            }}
            theme="dark"
            testid="tech-detail-payment-summary"
          />
        )}

        {/* Invoice panel — list + builder, tech can create + send */}
        <JobInvoicePanel
          job={job}
          role="tech"
          onChanged={reload}
          onView={(inv) => setViewInvoice(inv)}
        />

        {/* Timeline */}
        {job.timeline?.length > 0 && (
          <Section title="Timeline">
            <ol className="space-y-2">
              {[...job.timeline].reverse().map((t, i) => (
                <li key={i} className="text-xs flex items-start gap-2.5">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#FF6600] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[#FFFFFF] break-words">{t.event}</div>
                    <div className="text-[#FFFFFF]/45 mt-0.5">{prettyTime(t.ts)}</div>
                  </div>
                </li>
              ))}
            </ol>
          </Section>
        )}
      </div>

      {/* Sticky action bar */}
      {!isClosed && (
        <ActionBar
          job={job}
          busy={busy}
          onEnRoute={() => action(() => apiTech.post(`/tech/jobs/${job.id}/en-route`))}
          onOnSite={() => action(() => apiTech.post(`/tech/jobs/${job.id}/on-site`))}
          onPartOrdered={(note) => action(() => apiTech.post(`/tech/jobs/${job.id}/part-ordered`, { note }))}
          onQuote={(quoted, diagnosis) =>
            action(() => apiTech.post(`/tech/jobs/${job.id}/quote`, { quoted_price: quoted, diagnosis_note: diagnosis }))
          }
          onComplete={(payload) =>
            action(async () => {
              await apiTech.post(`/tech/jobs/${job.id}/complete`, payload);
              toast.success("Job completed");
              setTimeout(onClose, 600);
            })
          }
        />
      )}

      {/* Branded invoice viewer (overlay) */}
      {viewInvoice && (
        <div className="absolute inset-0 z-20 bg-[#FFFFFF]/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <BrandedInvoice invoice={viewInvoice} onBack={() => setViewInvoice(null)} />
        </div>
      )}
    </Sheet>
  );
}

// ============== Sheet (full-screen mobile drawer) ==============
function Sheet({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="absolute inset-0 sm:inset-auto sm:right-0 sm:top-0 sm:bottom-0 sm:w-[440px] bg-[#002060] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="tech-detail-sheet"
      >
        {children}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-[#002060] border border-[#FFFFFF]/15 rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-wider text-[#FFFFFF]/70 font-semibold mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Row({ icon: Icon, value }) {
  return (
    <div className="flex items-center gap-2.5 text-sm text-[#FFFFFF]">
      <Icon className="w-4 h-4 text-[#FFFFFF]/45 shrink-0" /> <span className="break-words">{value}</span>
    </div>
  );
}

function Action({ icon: Icon, label, href, external, testid, children }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      data-testid={testid}
      aria-label={label}
      className="flex items-center gap-2.5 -mx-2 px-2 py-2 rounded-md hover:bg-[#FF6600]/10 active:bg-[#FF6600]/10 transition-colors"
    >
      <Icon className="w-4 h-4 text-[#FF6600] shrink-0" />
      <span className="text-sm text-[#FF6600] truncate flex-1">{children}</span>
      <ChevronRight className="w-3.5 h-3.5 text-[#FFFFFF]/45 shrink-0" />
    </a>
  );
}

function KV({ k, v, tone = "slate" }) {
  const colors = { slate: "text-[#FFFFFF]", emerald: "text-[#FFFFFF]" };
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[#FFFFFF]/70">{k}</span>
      <span className={`font-semibold num capitalize ${colors[tone]}`}>{v}</span>
    </div>
  );
}

function prettyTime(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.valueOf())) return s;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// ============== Sticky action bar (en-route → on-site → quote → complete) ==============
function ActionBar({ job, busy, onEnRoute, onOnSite, onPartOrdered, onQuote, onComplete }) {
  const [mode, setMode] = useState(null); // null | 'quote' | 'complete' | 'part'
  return (
    <div className="sticky bottom-0 bg-[#002060] border-t border-[#FFFFFF]/15 p-3 space-y-2 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
      {mode === null && (
        <PrimaryActions
          job={job}
          busy={busy}
          onEnRoute={onEnRoute}
          onOnSite={onOnSite}
          openQuote={() => setMode("quote")}
          openComplete={() => setMode("complete")}
          openPart={() => setMode("part")}
        />
      )}
      {mode === "quote" && (
        <QuoteForm
          initialQuoted={job.quoted_price}
          initialDiagnosis={job.diagnosis_note}
          busy={busy}
          onCancel={() => setMode(null)}
          onSubmit={async (q, d) => { await onQuote(q, d); setMode(null); }}
        />
      )}
      {mode === "complete" && (
        <CompleteForm
          initialFinal={job.quoted_price ?? job.final_price}
          busy={busy}
          onCancel={() => setMode(null)}
          onSubmit={async (payload) => { await onComplete(payload); }}
        />
      )}
      {mode === "part" && (
        <PartOrderedForm
          busy={busy}
          onCancel={() => setMode(null)}
          onSubmit={async (note) => { await onPartOrdered(note); setMode(null); }}
        />
      )}
    </div>
  );
}

function PrimaryActions({ job, busy, onEnRoute, onOnSite, openQuote, openComplete, openPart }) {
  const s = job.status;
  return (
    <div className="grid grid-cols-1 gap-2">
      {s === "new" || s === "scheduled" ? (
        <Button
          data-testid="tech-act-enroute"
          disabled={busy}
          onClick={onEnRoute}
          className="w-full h-12 bg-[#FF6600] hover:bg-[#FF6600] text-[#FFFFFF] font-medium"
        >
          <Truck className="w-4 h-4 mr-1.5" /> I&apos;m en-route
        </Button>
      ) : null}

      {s === "en-route" ? (
        <Button
          data-testid="tech-act-onsite"
          disabled={busy}
          onClick={onOnSite}
          className="w-full h-12 bg-[#FF6600] hover:bg-[#FF6600] text-[#FFFFFF] font-medium"
        >
          <MapPinned className="w-4 h-4 mr-1.5" /> I&apos;ve arrived
        </Button>
      ) : null}

      {(s === "on-site" || s === "en-route" || s === "part-ordered") && (
        <div className="grid grid-cols-2 gap-2">
          <Button
            data-testid="tech-act-quote"
            variant="outline"
            disabled={busy}
            onClick={openQuote}
            className="h-12 border-[#FFFFFF]/15 text-[#FFFFFF] font-medium"
          >
            <FileText className="w-4 h-4 mr-1.5" /> {job.quoted_price ? "Re-quote" : "Add quote"}
          </Button>
          <Button
            data-testid="tech-act-complete"
            disabled={busy}
            onClick={openComplete}
            className="h-12 bg-[#FF6600] hover:bg-[#FF6600]/90 text-[#FFFFFF] font-semibold"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Complete
          </Button>
        </div>
      )}

      {/* Part-ordered: surfaced when tech is on-site (waiting for part) — and shown as 'awaiting' state if already in part-ordered */}
      {s === "on-site" && (
        <Button
          data-testid="tech-act-part-ordered"
          variant="outline"
          disabled={busy}
          onClick={openPart}
          className="w-full h-11 border-[#FFFFFF]/15 text-[#FFFFFF] font-medium"
        >
          <PackageOpen className="w-4 h-4 mr-1.5" /> Waiting for part
        </Button>
      )}
      {s === "part-ordered" && (
        <div className="text-center text-[11px] uppercase tracking-widest text-[#FF6600] font-bold py-1">
          <PackageOpen className="w-3 h-3 inline mr-1 -mt-0.5" /> Awaiting part — collect deposit on the invoice tab
        </div>
      )}

      {s === "new" || s === "scheduled" ? (
        <Button
          data-testid="tech-act-complete-direct"
          variant="outline"
          disabled={busy}
          onClick={openComplete}
          className="w-full h-11 border-[#FFFFFF]/15 text-[#FFFFFF] font-medium"
        >
          <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark complete
        </Button>
      ) : null}
    </div>
  );
}

function QuoteForm({ initialQuoted, initialDiagnosis, busy, onCancel, onSubmit }) {
  const [price, setPrice] = useState(initialQuoted ? String(initialQuoted) : "");
  const [diagnosis, setDiagnosis] = useState(initialDiagnosis || "");
  async function submit() {
    const p = parseFloat(price);
    if (!p || p <= 0) return toast.error("Enter a quoted price");
    await onSubmit(p, diagnosis.trim());
  }
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#FFFFFF]/70 font-semibold">
        <FileText className="w-3.5 h-3.5" /> Send quote
      </div>
      <div>
        <Label htmlFor="qf-price" className="text-[10px] uppercase tracking-wider text-[#FFFFFF]/70 font-medium">Quoted price ($)</Label>
        <Input
          id="qf-price"
          data-testid="tech-quote-price"
          type="number"
          inputMode="decimal"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="h-11 mt-1"
          autoFocus
        />
      </div>
      <div>
        <Label htmlFor="qf-diag" className="text-[10px] uppercase tracking-wider text-[#FFFFFF]/70 font-medium">Diagnosis (optional)</Label>
        <Textarea
          id="qf-diag"
          data-testid="tech-quote-diagnosis"
          value={diagnosis}
          onChange={(e) => setDiagnosis(e.target.value)}
          rows={2}
          className="mt-1"
          placeholder="What's wrong / what you'll do…"
        />
      </div>
      <div className="grid grid-cols-2 gap-2 pt-1">
        <Button variant="outline" onClick={onCancel} className="h-11 border-[#FFFFFF]/15 text-[#FFFFFF]" data-testid="tech-quote-cancel">Cancel</Button>
        <Button onClick={submit} disabled={busy} className="h-11 bg-[#FF6600] hover:bg-[#FF6600] text-[#FFFFFF]" data-testid="tech-quote-submit">Send quote</Button>
      </div>
    </div>
  );
}

function CompleteForm({ initialFinal, busy, onCancel, onSubmit }) {
  const [price, setPrice] = useState(initialFinal ? String(initialFinal) : "");
  const [parts, setParts] = useState("");
  const [method, setMethod] = useState("");
  const [note, setNote] = useState("");

  async function submit() {
    const p = parseFloat(price);
    if (!p || p <= 0) return toast.error("Enter a final price");
    const payload = { final_price: p };
    if (parts !== "") {
      const pc = parseFloat(parts);
      if (Number.isFinite(pc) && pc >= 0) payload.parts_cost = pc;
    }
    if (method) payload.payment_method = method;
    if (note.trim()) payload.diagnosis_note = note.trim();
    await onSubmit(payload);
  }

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#FFFFFF]/70 font-semibold">
        <ClipboardList className="w-3.5 h-3.5" /> Close out job
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#FFFFFF]/70 font-medium">Final $</Label>
          <Input
            data-testid="tech-complete-price"
            type="number"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="h-11 mt-1"
            autoFocus
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#FFFFFF]/70 font-medium">Parts $</Label>
          <Input
            data-testid="tech-complete-parts"
            type="number"
            inputMode="decimal"
            value={parts}
            onChange={(e) => setParts(e.target.value)}
            className="h-11 mt-1"
            placeholder="(optional)"
          />
        </div>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-[#FFFFFF]/70 font-medium flex items-center gap-1.5">
          <Banknote className="w-3 h-3" /> Payment method
        </Label>
        <div className="grid grid-cols-4 gap-1.5 mt-1.5">
          {PAYMENT_METHODS.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMethod(method === m.key ? "" : m.key)}
              data-testid={`tech-pay-${m.key}`}
              className={`h-10 rounded-md text-xs font-medium border transition ${
                method === m.key
                  ? "bg-[#FF6600] border-[#FF6600] text-[#FFFFFF]"
                  : "bg-[#002060] border-[#FFFFFF]/15 text-[#FFFFFF] hover:bg-[#FFFFFF]/10"
              }`}
            >{m.label}</button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-[#FFFFFF]/70 font-medium">Notes (optional)</Label>
        <Textarea
          data-testid="tech-complete-notes"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          className="mt-1"
          placeholder="What was repaired…"
        />
      </div>
      <div className="grid grid-cols-2 gap-2 pt-1">
        <Button variant="outline" onClick={onCancel} className="h-11 border-[#FFFFFF]/15 text-[#FFFFFF]" data-testid="tech-complete-cancel">Cancel</Button>
        <Button onClick={submit} disabled={busy} className="h-11 bg-[#FF6600] hover:bg-[#FF6600]/90 text-[#FFFFFF] font-semibold" data-testid="tech-complete-submit">
          Confirm complete
        </Button>
      </div>
    </div>
  );
}

function PartOrderedForm({ busy, onCancel, onSubmit }) {
  const [note, setNote] = useState("");
  return (
    <div className="space-y-3" data-testid="tech-part-form">
      <div className="text-[11px] uppercase tracking-widest text-[#FF6600] font-bold">
        Mark job as waiting for part
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-[#FFFFFF]/70 font-medium">Part / note (optional)</Label>
        <Textarea
          data-testid="tech-part-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. GE WR55X10942 ice maker — 2-3 days"
          rows={2}
          className="mt-1"
        />
      </div>
      <div className="text-xs text-[#FFFFFF]/70 leading-snug">
        Tip: now you can create an invoice on the Invoice tab and toggle <span className="text-[#FF6600] font-semibold">&quot;Collect Deposit&quot;</span> — 50% by default, fully editable.
      </div>
      <div className="grid grid-cols-2 gap-2 pt-1">
        <Button variant="outline" onClick={onCancel} className="h-11 border-[#FFFFFF]/15 text-[#FFFFFF]" data-testid="tech-part-cancel">Cancel</Button>
        <Button onClick={() => onSubmit(note)} disabled={busy} className="h-11 bg-[#FF6600] hover:bg-[#FF6600]/90 text-[#FFFFFF] font-semibold" data-testid="tech-part-submit">
          Confirm waiting for part
        </Button>
      </div>
    </div>
  );
}
